
import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { ArrowLeft, Check, CheckCheck, Loader2, MessageCircle, Send, Trash2, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";

const MobileMessages = () => {
  const [newMessage, setNewMessage] = useState("");
  const [replyTo, setReplyTo] = useState<{ id: string; sender: string } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id || null;

  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, []);

  const { data: messages, isLoading, isError, refetch } = useQuery({
    queryKey: ["messages"],
    queryFn: async () => {
      try {
        if (!currentUser) throw new Error("Non authentifié");

        const { data: messagesData, error } = await supabase
          .from("messages")
          .select(`
            *,
            sender:profiles!sender_id(*),
            receiver:profiles!receiver_id(*)
          `)
          .or(`sender_id.eq.${currentUser.id},receiver_id.eq.${currentUser.id}`)
          .order("created_at", { ascending: true });

        if (error) throw error;
        return messagesData;
      } catch (error) {
        console.error("Error fetching messages:", error);
        throw error;
      }
    },
    enabled: !!currentUser,
    retry: 3,
    retryDelay: 1000,
  });

  useEffect(() => {
    if (messages && !isLoading) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, isLoading, scrollToBottom]);

  const sendMessage = useMutation({
    mutationFn: async ({ content, receiverId }: { content: string; receiverId: string }) => {
      try {
        if (!currentUser) throw new Error("Non authentifié");

        const { error } = await supabase
          .from("messages")
          .insert({
            content,
            sender_id: currentUser.id,
            receiver_id: receiverId,
          });

        if (error) throw error;
      } catch (error) {
        console.error("Error sending message:", error);
        throw error;
      }
    },
    onSuccess: () => {
      setNewMessage("");
      setReplyTo(null);
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast.success("Message envoyé");
      setTimeout(scrollToBottom, 100);
    },
    onError: (error) => {
      console.error("Send message error:", error);
      toast.error("Erreur d'envoi du message");
    },
  });

  const deleteMessage = useMutation({
    mutationFn: async (messageId: string) => {
      try {
        const { error } = await supabase
          .from("messages")
          .delete()
          .eq("id", messageId);

        if (error) throw error;
      } catch (error) {
        console.error("Error deleting message:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      toast.success("Message supprimé");
      setMessageToDelete(null);
    },
    onError: (error) => {
      console.error("Delete message error:", error);
      toast.error("Erreur de suppression");
    },
  });

  const markAsRead = useMutation({
    mutationFn: async (messageId: string) => {
      try {
        const { error } = await supabase
          .from("messages")
          .update({ read: true })
          .eq("id", messageId);

        if (error) throw error;
      } catch (error) {
        console.error("Error marking message as read:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["messages"] });
      queryClient.invalidateQueries({ queryKey: ["unreadMessagesCount"] });
    },
    onError: (error) => {
      console.error("Mark as read error:", error);
    },
  });

  const handleReply = (messageId: string, senderName: string) => {
    setReplyTo({ id: messageId, sender: senderName });
  };

  const handleSendMessage = () => {
    if (!replyTo || !newMessage.trim()) return;

    const messageItem = messages?.find(m => m.id === replyTo.id);
    if (!messageItem) return;

    const receiverId = messageItem.sender_id === currentUserId 
      ? messageItem.receiver_id 
      : messageItem.sender_id;
      
    sendMessage.mutate({
      content: newMessage,
      receiverId: receiverId,
    });
  };

  const handleDeleteConfirm = () => {
    if (messageToDelete) {
      deleteMessage.mutate(messageToDelete);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteRequest = (messageId: string) => {
    setMessageToDelete(messageId);
    setDeleteDialogOpen(true);
  };

  const handleRetry = () => {
    refetch();
  };

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center p-6 h-[calc(100vh-150px)]">
        <p className="text-red-500 mb-4">Impossible de charger les messages</p>
        <Button onClick={handleRetry}>Réessayer</Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-6 h-[calc(100vh-150px)]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <div className="sticky top-0 z-10 border-b bg-white px-4 py-3 flex items-center justify-between shadow-sm">
        <Button 
          variant="ghost" 
          size="icon" 
          className="mr-2" 
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Retour</span>
        </Button>
        <h1 className="text-lg font-semibold text-gray-800">Messages</h1>
        <div className="w-8" />
      </div>

      {replyTo && (
        <div className="p-2 bg-blue-50 border-b flex items-center gap-2 shadow-sm">
          <div className="flex-1 text-sm truncate">
            <span className="text-blue-600">Répondre à: </span>
            <span className="font-medium">{replyTo.sender}</span>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-7 w-7"
            onClick={() => setReplyTo(null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {messages && messages.length > 0 ? (
        <ScrollArea className="flex-1 py-4 px-3 overflow-y-auto" ref={scrollAreaRef}>
          <div className="space-y-3 mb-1">
            {messages.map((message) => {
              const isFromMe = message.sender_id === currentUserId;
              const messageDate = new Date(message.created_at);
              
              return (
                <Card
                  key={message.id} 
                  className={cn(
                    "p-3 overflow-visible shadow-sm",
                    isFromMe 
                      ? "ml-auto bg-primary text-primary-foreground rounded-2xl rounded-tr-sm max-w-[85%]" 
                      : "mr-auto bg-white rounded-2xl rounded-tl-sm max-w-[85%]"
                  )}
                >
                  <div className="flex items-start gap-2">
                    {!isFromMe && (
                      <Avatar className="h-7 w-7 flex-shrink-0">
                        <AvatarImage src={message.sender?.profile_photo} />
                        <AvatarFallback className="text-xs bg-blue-100 text-blue-600">{message.sender?.full_name?.charAt(0) || "U"}</AvatarFallback>
                      </Avatar>
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex justify-between items-start gap-2">
                        <p className={cn("text-xs font-medium", isFromMe ? "text-primary-foreground/90" : "text-gray-700")}>
                          {isFromMe ? "Moi" : message.sender?.full_name || "Utilisateur"}
                        </p>
                        <p className={cn("text-[10px]", isFromMe ? "text-primary-foreground/70" : "text-gray-400")}>
                          {format(messageDate, 'HH:mm', { locale: fr })}
                        </p>
                      </div>
                      
                      <p className="mt-1 leading-snug break-words text-sm overflow-visible whitespace-normal">
                        {message.content}
                      </p>
                      
                      <div className="flex items-center justify-end gap-1 mt-1">
                        {!isFromMe && !message.read && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 rounded-full bg-blue-50 hover:bg-blue-100 p-0"
                            onClick={() => markAsRead.mutate(message.id)}
                            title="Marquer comme lu"
                          >
                            <Check className="h-3 w-3" />
                          </Button>
                        )}
                        {isFromMe && message.read && (
                          <span className="flex items-center text-primary-foreground/70 text-[10px]">
                            <CheckCheck className="h-3 w-3 mr-0.5" />
                            Lu
                          </span>
                        )}
                        {!isFromMe && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 rounded-full p-0 hover:bg-blue-100"
                            onClick={() => handleReply(message.id, message.sender?.full_name || "Utilisateur")}
                            title="Répondre"
                          >
                            <MessageCircle className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("h-5 w-5 rounded-full p-0", 
                            isFromMe ? "hover:bg-red-600 hover:text-white" : "hover:bg-red-100"
                          )}
                          onClick={() => handleDeleteRequest(message.id)}
                          title="Supprimer"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="bg-gray-100 rounded-full p-4 mb-4">
            <MessageCircle className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-700 font-medium">Aucun message</p>
          <p className="text-xs text-gray-500 mt-1 max-w-[250px]">
            Vos conversations s'afficheront ici lorsque vous commencerez à échanger des messages
          </p>
        </div>
      )}

      <div className="p-3 border-t bg-white sticky bottom-0 shadow-sm">
        <div className="flex items-center gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={replyTo ? `Répondre à ${replyTo.sender}...` : "Écrire un message..."}
            className="flex-1 bg-gray-50 border-gray-200"
          />
          <Button 
            size="icon" 
            className="rounded-full flex-shrink-0"
            disabled={!newMessage.trim() || !replyTo}
            onClick={handleSendMessage}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="max-w-[340px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le message</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer ce message ? Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MobileMessages;
